/**
 * Core contracts of the Living Lab OS.
 *
 * Everything the 3D world, the HTML overlay, the behavioral engine and the
 * AI interface exchange goes through these types. Keep them dependency-free —
 * they are imported from server and client code alike.
 */

export type ZoneId =
  | 'core'
  | 'research'
  | 'projects'
  | 'agents'
  | 'knowledge'
  | 'observatory'
  | 'archive'
  | 'gateway';

export const ZONES: ZoneId[] = [
  'core',
  'research',
  'projects',
  'agents',
  'knowledge',
  'observatory',
  'archive',
  'gateway',
];

/** Visual/operational state of the laboratory as a whole. */
export type LabStatus = 'idle' | 'active' | 'research' | 'computing' | 'discovery';

/** Rendering tiers negotiated by the Experience engine. */
export type ExperienceMode = 'ultra' | 'high' | 'balanced' | 'lite' | 'static';

export type AgentKind =
  | 'research'
  | 'knowledge'
  | 'architecture'
  | 'security'
  | 'experiment'
  | 'infrastructure'
  | 'product'
  | 'data'
  | 'review';

/**
 * What produced an agent's latest decision, read from the evidence chain:
 * `llm` — a recorded model consultation; `code-fallback` — the engine's
 * deterministic policy decided (possible only in engineering canaries);
 * `idle` — no decision in the observation window.
 */
export type AgentCognition = 'llm' | 'code-fallback' | 'idle';

/**
 * Locale-independent description of one agent action, produced by
 * `live-fold.ts` from a verified event and rendered per-locale by
 * `task-phrase.ts`. `k` names the template, `p` carries the already-bounded
 * data the engine reported (model names, peers, tick numbers). Nothing here
 * is invented: a phrase exists only where an event existed.
 */
export type TaskPhraseKey =
  | 'cognition.consulted'
  | 'cognition.unavailable'
  | 'cognition.starved'
  | 'cognition.rejected'
  | 'cognition.thought'
  | 'resource.spent'
  | 'task.claimed'
  | 'task.submitted'
  | 'task.evaluated'
  | 'link.created'
  | 'link.removed'
  | 'link.used'
  | 'message.sent'
  | 'message.delivered'
  | 'violation.recorded'
  | 'run.resumed'
  | 'run.started'
  | 'run.completed'
  | 'agent.created'
  | 'agent.retired'
  | 'agent.learning.updated'
  | 'task.created'
  | 'task.expired'
  | 'submission.verified'
  | 'memory.stored'
  | 'memory.retrieved'
  | 'capability.published'
  | 'capability.used'
  | 'resource.transferred'
  | 'pressure.applied'
  | 'metrics.recorded'
  | 'tick.completed'
  | 'event.other'
  | 'fold.retired'
  | 'fold.solving'
  | 'fold.awaiting'
  | 'fold.idle';

export interface TaskPhrase {
  k: TaskPhraseKey;
  p?: Readonly<Record<string, string | number | boolean>>;
}

export interface LabAgent {
  id: string;
  name: string;
  kind: AgentKind;
  role: string;
  /** Human-readable description of what the agent is doing right now (English). */
  task: string;
  /**
   * The same statement as `task`, structured so every locale can render it
   * from the engine's own facts instead of reading English machine prose.
   * Present only on observer-backed rows; the static fallback registry
   * carries hand-written per-id copy instead.
   */
  phrase?: TaskPhrase;
  /** Slug of a project or research entry the agent is attached to, if any. */
  attachedTo?: string;
  status: 'active' | 'idle' | 'moving';
  /** 0..1 — drives the activity bar and visual intensity. */
  activity: number;
  /** Ids of agents this one communicates with. */
  connections: string[];
  // ── Genesis-Live fields (present only on observer-backed rows) ────────
  /** What produced the agent's last decision (see AgentCognition). */
  cognition?: AgentCognition;
  /** Thinking tier of the last recorded consultation (fast/standard/deliberate). */
  tier?: string;
  /** Model named in the last recorded consultation. */
  model?: string;
  /** ANU generation counter of the agent. */
  generation?: number;
  /** Tick at which the agent left the world; absent while it is in it. */
  retiredTick?: number;
}

// ── Genesis-Live snapshot contracts ─────────────────────────────────────
// Produced server-side by the observer poller (src/lab/live-poller.ts) from
// the read-only ANU Observer, and consumed by /api/lab/agents,
// /api/lab/evidence and the SSE feed. Honesty contract: `live` is true only
// when the site itself verified the hash chain AND a living mind is on
// record (cognitionHealth); anything else is labelled STALE / SYNCING /
// OFFLINE with a reason.

export type Freshness = 'LIVE' | 'STALE' | 'OFFLINE' | 'SYNCING';

/**
 * Why a chain-verified world is not LIVE right now.
 * `code fallback` is the canary counterpart of `provider outage`: the run is
 * real and advancing, but nothing in the observation window was decided by a
 * model — the engine's deterministic policy decided it. A population that
 * thinks in code is not a living mind and must not read LIVE.
 */
export type StaleReason = 'no progress' | 'provider outage' | 'epoch boundary' | 'code fallback';

/**
 * Why nothing observer-backed can be shown at all.
 * `unreachable` is reserved for a transport failure (no answer at all);
 * `observer error` is an observer that DID answer, with something the site
 * cannot use (an HTTP error status, invalid JSON, an oversize body) — the
 * two are visibly different states to whoever operates the bridge.
 */
export type OfflineReason =
  | 'unconfigured'
  | 'unreachable'
  | 'observer error'
  | 'no run'
  | 'projection error';

export type FreshnessReason = StaleReason | OfflineReason;

/**
 * `live` — the Genesis-Live universe (experiment `genesis-live`, mode `live`);
 * `canary` — any other observer-backed run (engineering canary · code
 * fallback possible).
 */
export type LiveMode = 'live' | 'canary';

/** Consultations in the observation window, as recorded in the chain. */
export interface CognitionHealth {
  consulted: number;
  unavailable: number;
  starved: number;
}

/** Inclusive range of event sequence numbers the site verified itself. */
export interface ChainRange {
  from: number;
  to: number;
}

/** Flat link between two agents (LabLinkState of the engine, pre-P3). */
export interface LiveLink {
  id: string;
  left: string;
  right: string;
  strengthPpm: number;
  createdTick: number;
  lastUsedTick: number;
}

/**
 * Counters over the WHOLE observed world state — `activeAgents`,
 * `retiredAgents` and `links` are true statements about the universe, not
 * about the bounded roster served to the browser (see `rosterShown` and
 * `foldLiveState`'s roster contract).
 */
export interface LiveStats {
  tick: number;
  activeAgents: number;
  retiredAgents: number;
  links: number;
  tasksOpen: number;
  tasksCompleted: number;
  /**
   * Rows actually carried in `agents[]`: every active agent (up to
   * MAX_SERVED_ROSTER) plus the ones retired inside the observation window.
   * `rosterShown < activeAgents` means the cap dropped active agents — the
   * population is larger than the field shows, and any surface reporting a
   * population size must read `activeAgents`, never `agents.length`.
   */
  rosterShown: number;
}

/** A model that appears in cognition records of the current run. */
export interface LiveModelEvidence {
  provider: string;
  model: string;
  tier?: string;
  consultations: number;
  rejected: number;
  lastTick: number;
}

export interface LiveSnapshot {
  live: boolean;
  freshness: Freshness;
  reason?: FreshnessReason;
  source: 'anu-observer';
  /** ISO time of the last poll that produced this snapshot; null before the first. */
  observedAt: string | null;
  runId: string | null;
  universeId: string | null;
  experimentId: string | null;
  epoch: number | null;
  tick: number | null;
  seq: number | null;
  lastEventHash: string | null;
  chainVerified: ChainRange | null;
  cognitionHealth: CognitionHealth | null;
  mode: LiveMode | null;
  engineVersion: string | null;
  stats: LiveStats | null;
  links: LiveLink[];
  agents: LabAgent[];
  /** Monotonic per process; changes whenever the content above changes. */
  version: number;
}

/** Scalar-only entry of the epoch chain published by the observer. */
export type LiveChainEntry = Record<string, string | number | boolean | null>;

export interface LiveEvidence {
  source: 'anu-observer';
  freshness: Freshness;
  reason?: FreshnessReason;
  observedAt: string | null;
  runId: string | null;
  universeId: string | null;
  experimentId: string | null;
  epoch: number | null;
  mode: LiveMode | null;
  engineVersion: string | null;
  schemaVersion: number | null;
  policyId: string | null;
  cognitionId: string | null;
  head: { seq: number; tick: number; eventHash: string | null; completed: boolean } | null;
  chainVerified: ChainRange | null;
  attestationStatus: string | null;
  anchoredAt: string | null;
  boundary: boolean;
  cognitionHealth: CognitionHealth | null;
  models: LiveModelEvidence[];
  chain: LiveChainEntry[];
  /** Engine versions the vendored ANU package can project. */
  projectableEngineVersions: string[];
}

/**
 * A single entry of the live activity feed.
 * `kind` is an honesty contract: `simulated` events are ambient-simulation
 * flavour and are labeled as such in the UI; `real` events must be backed by
 * an actual system fact (content registry, build info, uptime).
 */
export interface LabEvent {
  id: string;
  ts: number;
  source: string;
  type: string;
  message: string;
  kind: 'simulated' | 'real';
  zone?: ZoneId;
}

export interface GraphNode {
  id: string;
  label: string;
  kind: 'domain' | 'topic' | 'method' | 'artifact';
  /** Ids of connected nodes (undirected; declare each edge once). */
  links: string[];
  research?: string[];
  projects?: string[];
}

export interface LaboratoryState {
  status: LabStatus;
  activeAgents: number;
  projects: number;
  researchDirections: number;
  knowledgeNodes: number;
  publications: number;
  updatedAt: string;
}

/** Inferred interest of the current visitor. Never identity, only intent. */
export type IntentId =
  | 'explorer'
  | 'researcher'
  | 'developer'
  | 'partner'
  | 'unknown';

export interface VisitorState {
  sessionStart: number;
  visitedZones: ZoneId[];
  viewedProjects: string[];
  viewedResearch: string[];
  agentInteractions: number;
  askLabUses: number;
  idleMs: number;
  intent: IntentId;
  intentConfidence: Partial<Record<IntentId, number>>;
}

/** A proactive, dismissible hint produced by the intent engine. */
export interface LabHint {
  id: string;
  text: string;
  actionLabel: string;
  href: string;
}
